import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useApp } from "../context/AppContext";
import { Logo } from "../components/Logo";
import api, { apiErr } from "../lib/api";
import { toast } from "sonner";
import { ArrowRight, ArrowLeft, Lock, Sparkles, DoorOpen } from "lucide-react";
import { StepFrame, PrimaryBtn, GhostBtn, GREEN, ART_LEAVES } from "../components/StepFrame";
import { ChoiceRow } from "./Onboarding";

export default function Assessment() {
    const { t, user } = useApp();
    const nav = useNavigate();
    const [questions, setQuestions] = useState([]);
    const [paid, setPaid] = useState(false);
    const [total, setTotal] = useState(18);
    const [answers, setAnswers] = useState({});
    const [idx, setIdx] = useState(0);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [phase, setPhase] = useState("q"); // q | preview
    const [preview, setPreview] = useState("");
    const [previewLoading, setPreviewLoading] = useState(false);
    const [previewErr, setPreviewErr] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const [q, a] = await Promise.all([api.get("/questions"), api.get("/assessment")]);
            setQuestions(q.data.questions);
            setPaid(q.data.paid_unlocked);
            setTotal(q.data.total);
            setAnswers(a.data.answers || {});
            const saved = a.data.current_index || 0;
            const clamped = Math.min(saved, q.data.questions.length);
            setIdx(clamped);
            if (!q.data.paid_unlocked && a.data.free_complete && clamped >= q.data.questions.length) {
                setPhase("preview");
            }
        } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
        setLoading(false);
    }, []);

    useEffect(() => { load(); }, [load]);

    useEffect(() => {
        if (phase === "preview" && !preview && !previewLoading) genPreview();
        // eslint-disable-next-line
    }, [phase]);

    const q = questions[idx];

    const setVal = (v) => setAnswers((a) => ({ ...a, [q.id]: v }));
    const raw = q ? answers[q.id] : undefined;
    const selOf = (r) => (r && typeof r === "object" ? (r.selected ?? r.value) : r);
    const otherOf = (r) => (r && typeof r === "object" ? (r.other || "") : "");
    const intensityOf = (r) => (r && typeof r === "object" ? r.intensity : undefined);

    const saveCurrent = async (nextIdx) => {
        if (!q) return;
        try {
            await api.post("/assessment/answer", { question_id: q.id, value: answers[q.id] ?? "", current_index: nextIdx });
        } catch (e) { /* non-blocking */ }
    };

    const canContinue = () => {
        if (!q) return false;
        const r = answers[q.id];
        if (q.type === "text") return true;
        if (q.type === "multi") { const s = selOf(r); return Array.isArray(s) && s.length > 0; }
        return selOf(r) != null && selOf(r) !== "";
    };

    const goNext = async () => {
        setSaving(true);
        const isLast = idx === questions.length - 1;
        await saveCurrent(Math.min(idx + 1, questions.length));
        setSaving(false);
        if (!isLast) { setIdx(idx + 1); return; }
        // last question of current set
        if (!paid) { setPhase("preview"); return; }
        // paid: all 18 done -> report
        nav("/report");
    };

    const goBack = async () => { await saveCurrent(idx); setIdx(Math.max(idx - 1, 0)); };
    const saveExit = async () => { await saveCurrent(idx); toast.success(t("common.saved")); nav("/profile"); };

    const genPreview = async () => {
        setPreviewLoading(true); setPreviewErr(false);
        try {
            const { data } = await api.post("/assessment/preview");
            setPreview(data.preview);
        } catch (e) { setPreviewErr(true); toast.error(apiErr(e.response?.data?.detail)); }
        setPreviewLoading(false);
    };

    if (loading) return <Center><Spinner /></Center>;

    // Preview + payment gate
    if (phase === "preview") {
        return (
            <div className="max-w-2xl mx-auto px-4 sm:px-8 py-10">
                <TopBar onExit={() => nav("/")} label={t("assess.free_tag")} />
                <div className="card-soft p-7 sm:p-9 mt-6">
                    <div className="flex items-center gap-2 text-sage"><Sparkles size={18} /><span className="uppercase tracking-[0.18em] text-xs font-semibold">{t("preview.title")}</span></div>
                    {previewLoading ? (
                        <div className="py-10 flex flex-col items-center gap-3 text-charcoal/60"><Spinner /><p className="text-sm">{t("report.generating")}</p></div>
                    ) : previewErr ? (
                        <div className="py-8 text-center">
                            <p className="text-sm text-charcoal/75">{t("report.retry_body")}</p>
                            <button data-testid="preview-retry" onClick={genPreview} className="btn-primary mt-4 px-5 py-2.5 text-sm">{t("common.retry")}</button>
                        </div>
                    ) : (
                        <p data-testid="preview-text" className="mt-4 text-charcoal/85 leading-relaxed whitespace-pre-line">{preview}</p>
                    )}
                    <p className="mt-4 text-xs text-charcoal/55">{t("preview.disclaimer")}</p>
                </div>

                <div className="card-soft p-7 sm:p-9 mt-6 bg-forest text-ivory">
                    <h2 className="text-2xl text-ivory font-serif">{t("preview.unlock_title")}</h2>
                    <p className="mt-3 text-ivory/80 text-sm leading-relaxed">{t("preview.unlock_body")}</p>
                    <div className="mt-5 flex items-center gap-2 text-gold"><Lock size={16} /><span className="text-2xl font-serif">{t("preview.price")}</span></div>
                    <button data-testid="unlock-payment-button" onClick={() => nav("/payment")} className="mt-5 w-full rounded-full bg-gold text-charcoal py-3.5 font-semibold hover:brightness-105 transition">{t("preview.cta")}</button>
                </div>
            </div>
        );
    }

    if (!q) return (
        <div className="max-w-xl mx-auto px-4 py-24 text-center" data-testid="assessment-complete">
            <h1 className="text-3xl sm:text-4xl text-forest">You've completed your reflection</h1>
            <p className="mt-3 text-charcoal/70">All 18 questions are saved. Open your personalized report.</p>
            <button data-testid="assess-view-report" onClick={() => nav("/report")} className="btn-primary mt-6 px-6 py-3 font-medium">{t("nav.report")}</button>
        </div>
    );

    const progressNum = idx + 1;
    const sel = selOf(raw);
    const isLast = idx === questions.length - 1;
    const nextLabel = saving ? t("common.saving") : (isLast ? (paid ? t("nav.report") : t("assess.gen_preview")) : t("common.continue"));

    return (
        <StepFrame onBack={idx > 0 ? goBack : saveExit} right={<button data-testid="assess-exit" onClick={saveExit} className="inline-flex items-center gap-1.5 hover:underline"><DoorOpen size={13} /> {t("assess.saveexit")}</button>}
            stepLabel={<span data-testid="assess-progress">Step {progressNum} of {total}</span>} progress={(progressNum / total) * 100}
            title={q.tier === "free" ? "A Few Questions About You" : "Going a Little Deeper"} subtitle="Answer honestly. There are no right or wrong answers." art={ART_LEAVES}
            footer={<>
                <GhostBtn data-testid="assess-back" onClick={goBack} disabled={idx === 0}><ArrowLeft size={15} /> {t("common.back")}</GhostBtn>
                <PrimaryBtn data-testid="assess-continue" onClick={goNext} disabled={!canContinue() || saving}>{nextLabel} <ArrowRight size={15} /></PrimaryBtn>
            </>}>
            <div className="rounded-2xl p-6 sm:p-8" style={{ background: "#FBF8F1", border: `1px solid ${GREEN}1A` }} key={q.id}>
                {q.scenario && <p className="text-sm italic mb-3" style={{ color: "#6B766F" }}>{q.scenario}</p>}
                <h2 className="font-serif text-xl sm:text-2xl leading-snug" style={{ color: GREEN }}>{q.prompt}</h2>

                {q.type === "choice" && (
                    <div className="mt-6 grid gap-2.5">
                        {q.options.map((opt) => (
                            <ChoiceRow key={opt} data-testid="assessment-option-item" on={sel === opt} label={opt}
                                onClick={() => setVal(otherOf(raw) ? { selected: opt, other: otherOf(raw) } : opt)} />
                        ))}
                    </div>
                )}

                {q.type === "multi" && (
                    <div className="mt-6 grid sm:grid-cols-2 gap-2.5">
                        {q.options.map((opt) => {
                            const arr = Array.isArray(sel) ? sel : [];
                            const on = arr.includes(opt);
                            const toggle = () => setVal({ selected: on ? arr.filter((x) => x !== opt) : [...arr, opt], intensity: intensityOf(raw), other: otherOf(raw) });
                            return <ChoiceRow key={opt} data-testid="assessment-option-item" on={on} label={opt} onClick={toggle} round={false} />;
                        })}
                    </div>
                )}
                {q.type === "multi" && q.intensity && (
                    <div className="mt-6">
                        <label className="text-sm" style={{ color: "#4E5A54" }}>{t("assess.intensity")}</label>
                        <input data-testid="intensity-slider" type="range" min="0" max="10" value={intensityOf(raw) ?? 5}
                            onChange={(e) => setVal({ selected: Array.isArray(sel) ? sel : [], intensity: Number(e.target.value), other: otherOf(raw) })}
                            className="w-full mt-2 accent-[#1F3A30]" />
                        <div className="text-center font-serif text-2xl" style={{ color: GREEN }}>{intensityOf(raw) ?? 5}</div>
                    </div>
                )}

                {q.type === "text" && (
                    <textarea data-testid="assessment-text-input" rows={4} value={typeof raw === "string" ? raw : (raw?.value || "")}
                        onChange={(e) => setVal(e.target.value)} placeholder={t("assess.text_placeholder")}
                        className="mt-6 w-full rounded-lg px-4 py-3 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[#1F3A30]/30" style={{ border: `1px solid ${GREEN}26` }} />
                )}

                {q.allow_other && q.type !== "text" && (
                    <input data-testid="assessment-other-input" value={otherOf(raw)} placeholder={t("assess.other")}
                        onChange={(e) => {
                            if (q.type === "multi") setVal({ selected: Array.isArray(sel) ? sel : [], intensity: intensityOf(raw), other: e.target.value });
                            else setVal({ selected: sel || "", other: e.target.value });
                        }}
                        className="mt-4 w-full rounded-lg px-4 py-2.5 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[#1F3A30]/30" style={{ border: `1px solid ${GREEN}26` }} />
                )}
            </div>
            <p className="mt-4 text-xs" style={{ color: "#6B766F" }}>{t("assess.reassure")}</p>
        </StepFrame>
    );
}

const Center = ({ children }) => <div className="min-h-[60vh] flex items-center justify-center">{children}</div>;
const Spinner = () => <div className="h-8 w-8 rounded-full border-2 border-forest/20 border-t-forest animate-spin" />;
const TopBar = ({ onExit, exitLabel, label }) => (
    <div className="flex items-center justify-between">
        <div className="text-lg"><Logo /></div>
        <div className="flex items-center gap-3">
            <span className="text-xs uppercase tracking-[0.15em] text-sage font-semibold">{label}</span>
            {onExit && <button data-testid="assess-exit" onClick={onExit} className="inline-flex items-center gap-1.5 text-xs text-charcoal/60 hover:text-forest"><DoorOpen size={14} /> {exitLabel || ""}</button>}
        </div>
    </div>
);
